import { Quill } from 'react-quill-new';

/**
 * El botón, **dentro** del cuerpo del correo.
 *
 * ── Por qué va aquí y no en una lista aparte ───────────────────────────────
 * Antes los botones eran una lista debajo del editor y siempre salían al final
 * del correo. Dos problemas, y el segundo era el gordo:
 *
 *   1. No se podía decidir **dónde** va el botón. Un aviso de pago quiere el
 *      botón justo después de la frase que lo pide, no al final del todo.
 *   2. **Las dos vistas previas no lo pintaban**, porque se montan a partir del
 *      cuerpo y el botón no estaba en el cuerpo. Hacías clic, aparecía una fila
 *      arriba, y abajo no cambiaba nada: la previa afirmaba por omisión que el
 *      correo no llevaba botón.
 *
 * Viviendo dentro del cuerpo, lo segundo deja de poder pasar: lo que se ve es
 * lo que hay, porque sale del mismo sitio.
 *
 * ── Qué se guarda ──────────────────────────────────────────────────────────
 * `{ insert: { button: { label, href } } }`. El `href` puede ser una URL o una
 * variable (`{{payment_url}}`), que se resuelve al enviar con el enlace de pago
 * de ese expediente.
 *
 * Lo que se pinta aquí es **una representación**, no el botón del correo: ese lo
 * monta el backend con tablas, porque Outlook no entiende otra cosa. Pero se
 * pinta **con la misma cara**: el color de la marca de la empresa, el mismo
 * tamaño y la misma forma, que es lo que decide `EditorCuerpo`. Mientras esto
 * fue un azul fijo distinto del de la empresa, lo que se veía al escribir no
 * era lo que le llegaba al deudor y nadie podía notarlo sin mandarse un correo.
 *
 * El destino va **debajo**, en letra pequeña, y no al lado: pegado al botón
 * parecía parte del diseño, cuando en el correo no se ve. Hace falta que se lea
 * —si no, nadie sabe adónde apunta— pero fuera de la línea del botón.
 *
 * 🔴 **Los nombres de estas clases no llevan `_`.** Quien las viste es un
 * selector arbitrario de Tailwind (`[&_.ar-boton-cara]:…` en `EditorCuerpo`), y
 * ahí el guion bajo **es un espacio**: mientras la cara se llamó
 * `ar-boton-editor` + dos guiones bajos + `cara`, el CSS salió como
 * `.ar-boton-editor cara` y el botón del editor **no tuvo ningún estilo**, sin
 * que nada diera error.
 */

const BlockEmbed = Quill.import('blots/block/embed');

class BotonBlot extends BlockEmbed {
  static create(valor) {
    const nodo = super.create();
    const { label = '', href = '' } = valor || {};

    nodo.setAttribute('data-label', label);
    nodo.setAttribute('data-href', href);
    // No editable: el texto se cambia en el panel de abajo, no tecleando dentro
    // del botón. Si fuera editable, media palabra escrita a medias acabaría en
    // el correo sin que nadie lo viera.
    nodo.setAttribute('contenteditable', 'false');
    nodo.className = 'ar-boton-editor';

    const cara = document.createElement('span');
    cara.className = 'ar-boton-cara';
    cara.textContent = label || 'Botón sin texto';
    nodo.appendChild(cara);

    const destino = document.createElement('span');
    destino.className = 'ar-boton-destino';
    destino.textContent = href || 'sin destino';
    nodo.appendChild(destino);

    return nodo;
  }

  static value(nodo) {
    return {
      label: nodo.getAttribute('data-label') || '',
      href: nodo.getAttribute('data-href') || '',
    };
  }
}

BotonBlot.blotName = 'button';
BotonBlot.tagName = 'div';
BotonBlot.className = 'ar-boton-editor';

Quill.register(BotonBlot, true);

export default BotonBlot;
